import { createContext, useContext, useState, useCallback } from 'react';

/**
 * ContentPanelContext
 * Shared state for ContentPanel and its children.
 * Avoids prop drilling for panel open state, focus mode, edit mode and note options panel position.
 */
const ContentPanelContext = createContext(null);

/**
 * ContentPanelProvider
 * Wrap ContentPanel root with this provider.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
export function ContentPanelProvider({ children }) {
    // Note options panel
    const [panelOpen, setPanelOpen] = useState(false);
    const [panelPos, setPanelPos] = useState({ top: 60, left: 200 });

    // Focus mode
    const [inFocusMode, setInFocusMode] = useState(false);

    // Edit body mode — shared so NoteOptionsPanelActions can disable relevant actions
    const [isEditing, setIsEditing] = useState(false);

    /** Opens the note options panel at the given position */
    const openPanel = useCallback((pos) => {
        if (pos) setPanelPos(pos);
        setPanelOpen(true);
    }, []);

    /** Closes the note options panel */
    const closePanel = useCallback(() => setPanelOpen(false), []);

    return (
        <ContentPanelContext.Provider value={{
            panelOpen, panelPos, openPanel, closePanel,
            inFocusMode, setInFocusMode,
            isEditing, setIsEditing,
        }}>
            {children}
        </ContentPanelContext.Provider>
    );
}

/**
 * useContentPanelContext
 * Hook to consume ContentPanelContext.
 * Must be used inside a ContentPanelProvider.
 * @returns {{ panelOpen, panelPos, openPanel, closePanel, inFocusMode, setInFocusMode, isEditing, setIsEditing }}
 */
export function useContentPanelContext() {
    const ctx = useContext(ContentPanelContext);
    if (!ctx) throw new Error('useContentPanelContext must be used inside ContentPanelProvider');
    return ctx;
}
